import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { compareEntities, isEquals } from '@lycoris-nubila/ngx-lycoris';
import { isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import {
	UserAnswerState,
	UserAnswerStore,
} from 'user/answer/user-answer.store';
import { UserModuleAnswerRepositoryPort } from 'user/port/module/answer/user-module-answer-repository.port';
import { ObserveUserAnswersByModulesIdQuery } from 'user/query/module/answer/observe-user-answers-by-modules-id.query';
import { UserModuleAnswer } from 'user/user-module-answer';

import { CacheObservable } from '../../../app-decorators';

@Injectable()
export class UserModuleAnswerRepositoryAdapter
	extends QueryEntity<UserAnswerState>
	implements UserModuleAnswerRepositoryPort
{
	constructor(protected store: UserAnswerStore) {
		super(store);
	}

	@CacheObservable()
	public observeUserAnswersByModulesId(
		query: ObserveUserAnswersByModulesIdQuery
	): Observable<UserModuleAnswer[]> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.userId, query.userId) &&
				isEquals(entity.reportTaskId, query.reportTaskId) &&
				isEquals(entity.reportJobOfferId, query.reportJobOfferId) &&
				(query.allContacts || isEquals(entity.contactId, query.contactId)) &&
				((query.modulesId || []).includes(entity.moduleId) ||
					(query.modulesId || []).includes(entity.orgModuleId)),
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeUserAnswersByQuestionsId(
		userId: string,
		questionsId: string[]
	): Observable<UserModuleAnswer[]> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.userId, userId) &&
				(questionsId || []).includes(entity.questionId),
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async removeUserAnswersByIds(answersId: string[]): Promise<void> {
		return this.store.remove(answersId);
	}

	public async saveUserAnswers(entities: UserModuleAnswer[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
